<template>
	<div>
		<div class="panel-header panel-header-custom bg-primary-gradient">
			<div class="page-inner py-5">
				<div class="d-flex align-items-left align-items-md-center flex-column flex-md-row">
					<div>
						<h2 class="text-white pb-2 fw-bold">{{ $t('Профиль колледжа') }}</h2>
					</div>
					<div class="ml-md-auto py-2 py-md-0">
						<a
							v-if="!editMode"
							class="btn btn-secondary btn-round white--text"
							@click.stop="setEditMode"
						>{{ $t('Редактировать') }}</a>
						<a
							v-else
							class="btn btn-secondary btn-round white--text"
							:class="{ disabled: isSaving }"
							@click.stop="submitChange"
						>{{ $t('Сохранить') }}</a>
					</div>
				</div>
			</div>
		</div>
		<div class="page-inner mt--5">
			<v-fade-transition mode="out-in">
				<v-card
					v-if="isLoading"
					key="profile-loader"
					elevation="2"
					class="mb-5"
					aria-busy="true"
					aria-live="polite"
				>
					<v-card-text class="request-data card-text-custom pt-4">
						<v-row>
							<v-col
								lg="4"
								md="6"
								cols="12"
								v-for="n in profileSkeletonFieldCount"
								:key="'profile-skeleton-' + n"
							>
								<v-skeleton-loader
									type="text"
									class="places-skeleton-loader"
								></v-skeleton-loader>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
				<v-card v-else-if="hasProfileFields" key="profile-content" elevation="2" class="mb-5">
				<v-card-text class="request-data card-text-custom pt-4">
					<v-form class="form" ref="profileForm">
						<v-row>
							<v-col lg="4" md="6" cols="12" v-for="(item, itemKey) in profileFields" :key="itemKey">
								<v-text-field
									v-if="item.type == 'string'"
									v-model="item.value"
									:label="fieldLabel(item)"
									type="text"
									:rules="[v => !item.is_required || !!v || $t('Обязательное поле')]"
									:disabled="!editMode || !item.can_change">
								</v-text-field>
								<template v-else-if="item.type == 'metadata'">
									<v-text-field
										v-if="!editMode || !item.can_change"
										:value="fieldDisplayValue(item)"
										:label="fieldLabel(item)"
										type="text"
										disabled>
									</v-text-field>
									<v-select
										v-else
										v-model="item.id"
										:items="metadataSelectOptions[itemKey] || []"
										item-text="name"
										item-value="id"
										:label="fieldLabel(item)"
										:rules="[v => v !== null && v !== undefined && v !== '' || $t('Обязательное поле')]"
										@change="onMetadataFieldChange(itemKey, $event)"
									></v-select>
								</template>
								<template v-else-if="item.type == 'handbook'">
									<v-text-field
										v-if="!editMode || !item.can_change"
										:value="fieldDisplayValue(item)"
										:label="fieldLabel(item)"
										type="text"
										disabled>
									</v-text-field>
									<template v-else>
										<v-select
											v-model="item.value"
											:items="handbookSelectOptions(itemKey)"
											item-text="name"
											item-value="id"
											:label="fieldLabel(item)"
											:rules="[v => !item.is_required || Object.keys(v).length != 0 || $t('Обязательное поле')]"
											return-object>
										</v-select>
									</template>
								</template>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>
			</v-card>
			</v-fade-transition>
		</div>
	</div>
</template>

<script>
	import {
		applyCollegeProfileUpdates,
		buildEditCollegeFormData,
		buildCollegeProfileFormPayload,
		cloneCollegeProfileValues,
		filterCollegeProfileValues,
		getChangedCollegeProfilePayload,
		getCollegeProfileMetadataOptions,
		mapCollegeInfoCollegeProfile,
	} from '@/utils/college';
	import { getMetadataLabel } from '@/utils/metadata';

	export default {
		mounted() {
			this.getData();
		},
		computed: {
			usesCollegeNewApi() {
				return this.$store.getters.usesCollegeNewApi;
			},
			profileFields() {
				return filterCollegeProfileValues(this.profileInfo.values);
			},
			hasProfileFields() {
				return Object.keys(this.profileFields).length > 0;
			},
			profileSkeletonFieldCount() {
				const fieldCount = Object.keys(this.profileFields).length;
				return fieldCount > 0 ? fieldCount : 9;
			},
			metadataSelectOptions() {
				const options = getCollegeProfileMetadataOptions(this.collegeMetadata);
				const translated = {};

				Object.keys(options).forEach((key) => {
					translated[key] = (options[key] || []).map((option) => ({
						...option,
						name: this.$t(String(option.name)),
					}));
				});

				return translated;
			},
		},
		data: () => ({
			isLoading: true,
			isSaving: false,
			profileInfo: {},
			collegeMetadata: {},
			profileSnapshot: null,
			editMode: false,
			handbooks: {},
		}),
		methods: {
			fieldLabel(item) {
				return this.$t(item.labelKey || item.name);
			},

			fieldDisplayValue(item) {
				if (item.type === 'handbook') {
					return item.value && item.value.name ? this.$t(String(item.value.name)) : '';
				}

				if (item.type === 'metadata') {
					return item.value ? this.$t(String(item.value)) : '';
				}

				return item.value != null ? String(item.value) : '';
			},

			handbookSelectOptions(itemKey) {
				return (this.handbooks[itemKey] || []).map((option) => ({
					...option,
					name: this.$t(String(option.name)),
				}));
			},

			onMetadataFieldChange(itemKey, selectedId) {
				const item = this.profileInfo.values[itemKey];
				if (!item) {
					return;
				}
				const metadataKey = item.metadataKey || itemKey;
				item.id = selectedId;
				item.value = getMetadataLabel(this.collegeMetadata[metadataKey], selectedId);
			},
			async setEditMode() {
				if (this.usesCollegeNewApi) {
					if (!Object.keys(this.collegeMetadata).length) {
						await this.loadCollegeMetadata();
					}
					this.profileSnapshot = buildCollegeProfileFormPayload(this.profileInfo.values);
					this.editMode = true;
					return;
				}

				for (const key in this.profileFields) {
					const item = this.profileFields[key];
					if (item.type == 'handbook' && item.can_change && !this.handbooks[key]) {
						await this.$http.get('/handbook/' + key).then((response) => {
							this.handbooks[key] = response.data.data;
						});
					}
				}
				this.editMode = true;
			},
			async loadCollegeMetadata() {
				const response = await this.$http.get('/metadata');
				this.collegeMetadata = (response.data && response.data.data) || response.data || {};
			},
			async submitChange() {
				if (!this.$refs.profileForm.validate() || this.isSaving) {
					return;
				}

				this.isSaving = true;
				try {
					if (this.usesCollegeNewApi) {
						const changed = getChangedCollegeProfilePayload(
							this.profileInfo.values,
							this.profileSnapshot
						);

						if (!Object.keys(changed).length) {
							this.editMode = false;
							this.profileSnapshot = null;
							return;
						}

						const formData = buildEditCollegeFormData(changed);
						const savedValues = cloneCollegeProfileValues(this.profileInfo.values);

						const response = await this.$http.post('/edit-college', formData);
						if (!response.data || !response.data.status) {
							this.$store.dispatch('alertAdd', {
								text: (response.data && response.data.message) || this.$t('Ошибка'),
								type: 'error',
							});
							return;
						}

						await this.getData({ cacheBust: true });
						applyCollegeProfileUpdates(this.profileInfo.values, changed, savedValues);
						this.profileInfo = {
							...this.profileInfo,
							values: { ...this.profileInfo.values },
						};
						this.$store.dispatch('alertAdd', {
							text: (response.data && response.data.message) || this.$t('Изменения успешно сохранены'),
							type: 'success',
						});
						this.editMode = false;
						this.profileSnapshot = null;
						return;
					}

					const payload = {
						...this.profileInfo,
						values: filterCollegeProfileValues(this.profileInfo.values),
					};
					await this.$http.post('/profile', payload);
					await this.getData();
					this.$store.dispatch('alertAdd', {
						text: this.$t('Изменения успешно сохранены'),
						type: 'success',
					});
					this.editMode = false;
				} finally {
					this.isSaving = false;
				}
			},
			async getData(options = {}) {
				const showSkeleton = !this.hasProfileFields;
				if (showSkeleton) {
					this.isLoading = true;
				}

				if (this.usesCollegeNewApi) {
					try {
						const infoRequestConfig = options.cacheBust
							? { params: { _t: Date.now() } }
							: undefined;
						const [infoResponse, metadataResponse] = await Promise.all([
							this.$http.get('/info-college', infoRequestConfig),
							this.$http.get('/metadata'),
						]);
						this.collegeMetadata = (metadataResponse.data && metadataResponse.data.data) || metadataResponse.data || {};

						if (!infoResponse.data || !infoResponse.data.status) {
							this.profileInfo = { values: {} };
							return;
						}

						const data = (infoResponse.data && infoResponse.data.data) || infoResponse.data || {};
						this.profileInfo = mapCollegeInfoCollegeProfile(
							data,
							(key) => this.$t(key),
							this.collegeMetadata
						);
					} finally {
						if (showSkeleton) {
							this.isLoading = false;
						}
					}
					return;
				}

				try {
					const response = await this.$http.get('/profile');
					const collegeProfile = response.data.data.college_profile || {};
					this.profileInfo = {
						...collegeProfile,
						values: filterCollegeProfileValues(collegeProfile.values),
					};
				} finally {
					if (showSkeleton) {
						this.isLoading = false;
					}
				}
			},
		},
	};
</script>

<style type="text/css" scoped src="@/assets/css/college/PlaceListView.css"></style>
